import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { OnAppInitHandler } from '@casinocore/platform';
import { CasinoCoreNpmParams, CasinoLobbyService, ConfigProviderService, OobeeIntimationToasterService } from '@casinocore/platform/core';
import { CookieService, NavigationService, OnAppInit, ProductService, UserLoginEvent, UserService } from '@frontend/vanilla/core';
import { rxEffects } from '@rx-angular/state/effects';
import { filter } from 'rxjs';

import { FeaturesClientConfig, GlobalClientConfig, McPageClientConfig } from '../client-config';
import { WindowRef } from '../shared/windowref.service';

const CASINO_DARK_MODE_CSS_CLASS = 'casino-darkmode';
const BACKGROUND_COLOR_ATTRIBUTE = 'data-bg-color';
const DARK_MODE = 'dark-mode';
const CASINO_BODY_CSS_CLASS = 'casino-body';
const BACKGROUND_COLOR_PROPERTY = 'background-color';
const HIDE_BINGO_TOURNAMENTS_CSS_CLASS = 'hide-bingo-tournaments';

@Injectable({ providedIn: 'root' })
export class CasinoBootstrapService implements OnAppInit {
    private readonly user = inject(UserService);
    private readonly navigation = inject(NavigationService);
    private readonly mcPage = inject(McPageClientConfig);
    private readonly global = inject(GlobalClientConfig);
    private readonly oobeeIntimationToasterService = inject(OobeeIntimationToasterService);
    private readonly cookie = inject(CookieService);
    private readonly casinoLobbyService = inject(CasinoLobbyService);
    private readonly onAppInitHandler = inject(OnAppInitHandler);
    private readonly featuresConfig = inject(FeaturesClientConfig);
    private readonly configProviderService = inject(ConfigProviderService);
    private readonly windowRef = inject(WindowRef);
    private readonly productService = inject(ProductService);

    private document = inject(DOCUMENT);
    private readonly rxEffects = rxEffects();

    previousUrl = this.mcPage.prefix;

    onAppInit() {
        //To allow hashes in URL eg, https://casino.pa.betmgm.com/en/games#hollywoodcasino
        //We need this as we are still using this
        if (this.featuresConfig.enableHashText) {
            const currUrl = this.navigation.location.absUrl();
            const hashTextExists = currUrl.includes(this.featuresConfig.hashText);

            if (!hashTextExists) {
                this.navigation.goTo(`${currUrl}#${this.featuresConfig?.hashText}`, { replace: true });
            }
        }

        this.setPrerenderReadyOnLobbyLoad();
        // set default casinoCodeNPMParams
        const casinoCoreNpmParams: CasinoCoreNpmParams = new CasinoCoreNpmParams();
        casinoCoreNpmParams.isDll = true;
        casinoCoreNpmParams.disableFullLobbyFetch = true;
        //This is to initialise client config and store it in localStorage
        this.casinoLobbyService.initiateCasinoLobby(casinoCoreNpmParams);
        this.displayToasterOnUserLogin();
        // For Single Domain triggring this method on product activate. Eles it is triggring twice
        if (!this.productService.isSingleDomainApp) {
            this.activateStyleForCasino();
        }
        // To load all the required services and initialise them
        this.onAppInitHandler.execute();
    }

    deactivateStyles(): void {
        this.document.body.classList.remove(CASINO_BODY_CSS_CLASS);
        this.updateBackgroundColorOnLocationChange();
        this.document.getElementsByTagName('html')[0].classList.remove(HIDE_BINGO_TOURNAMENTS_CSS_CLASS);
        this.document.getElementsByTagName('html')[0].classList.remove(CASINO_DARK_MODE_CSS_CLASS);
        this.removeClassToBodyForMultilabel();
        this.removeBackgroundColor();
    }

    activateStyleForCasino(): void {
        //It impacts the background color mostly which should ideally be handled on theme, Check if we could get rid of this
        this.document.body.classList.add(CASINO_BODY_CSS_CLASS);
        this.updateBackgroundColorOnLocationChange();
        // To hide the tourments div space while they are being fetched as it resulted in flicker
        // Should push this to component level
        this.document.getElementsByTagName('html')[0].classList.add(HIDE_BINGO_TOURNAMENTS_CSS_CLASS);
        this.toggleCasinoDarkModeCssClass();
        this.addClassToBodyForMultilabel();
    }

    private setPrerenderReadyOnLobbyLoad(): void {
        this.rxEffects.register(this.casinoLobbyService.lobbyLoadedObservable, (result) => {
            if (result) {
                setTimeout(() => {
                    const casinoPrerenderElement = this.document.getElementById('casino-prerender');
                    if (casinoPrerenderElement) {
                        casinoPrerenderElement.innerHTML = ' window.prerenderReady = true';
                        this.windowRef.nativeWindow.prerenderReady = true;
                    }
                }, 0);
            }
        });
    }

    private updateBackgroundColorOnLocationChange() {
        const featuresConfig: any = this.configProviderService.provideFeaturesConfig();
        const nonCasinoUrlPaths = featuresConfig?.nonCasinoUrls ? JSON.parse(featuresConfig.nonCasinoUrls) : [];
        this.rxEffects.register(this.navigation.locationChange, (e) => {
            //If we are coming from labelhost / account the background color was staying as is, we need this to update the bg color according to user logged in status
            const nextUrl = e?.nextUrl;
            //TODO: This returns true for empty string as well , do we want it to work like this?
            const values = nonCasinoUrlPaths?.map((m: any) => nextUrl.toLocaleLowerCase().indexOf(m.toLocaleLowerCase()) > -1);
            const isTrueValue = values.indexOf(true) > -1;
            if (nextUrl && isTrueValue) {
                const casinoLobbyBgColor: string = this.document.body.getAttribute(BACKGROUND_COLOR_ATTRIBUTE) || '';
                if (casinoLobbyBgColor === this.global.lobbyConfigurations.lobbyBackgroundColor) {
                    this.document.body.removeAttribute(BACKGROUND_COLOR_ATTRIBUTE);
                    this.document.body.style.removeProperty(BACKGROUND_COLOR_PROPERTY);
                }
            }
        });
    }

    private displayToasterOnUserLogin() {
        this.rxEffects.register(this.user.events.pipe(filter((e: any) => e instanceof UserLoginEvent)), () => {
            this.oobeeIntimationToasterService.display();
        });
    }

    private getFromCookie(key: string): boolean {
        return this.cookie.get(key) > '0';
    }

    private setBackgroundColor() {
        //Check if we could we push this at component level
        this.document.body.style.backgroundColor = this.global.lobbyConfigurations.lobbyBackgroundColor;
        this.document.body.setAttribute(BACKGROUND_COLOR_ATTRIBUTE, this.global.lobbyConfigurations.lobbyBackgroundColor);
    }

    private removeBackgroundColor() {
        this.document.body.style.backgroundColor = '';
        this.document.body.removeAttribute(BACKGROUND_COLOR_ATTRIBUTE);
    }

    private toggleCasinoDarkModeCssClass() {
        if (this.getFromCookie(DARK_MODE)) {
            this.document.getElementsByTagName('html')[0].classList.add(CASINO_DARK_MODE_CSS_CLASS);
            return;
        }
        this.document.getElementsByTagName('html')[0].classList.remove(CASINO_DARK_MODE_CSS_CLASS);
        this.setBackgroundColor();
    }

    private addClassToBodyForMultilabel() {
        const { multiLabelConfiguration, enableMultiLabelConfiguration } = this.featuresConfig;
        if (enableMultiLabelConfiguration) {
            const labelConfig = multiLabelConfiguration[window.location.host];
            if (labelConfig) {
                const body = this.document.body;
                body.classList.add(labelConfig.className);
            }
        }
    }

    private removeClassToBodyForMultilabel() {
        const { multiLabelConfiguration, enableMultiLabelConfiguration } = this.featuresConfig;
        if (enableMultiLabelConfiguration) {
            const labelConfig = multiLabelConfiguration[window.location.host];
            if (labelConfig) {
                const body = this.document.body;
                body.classList.remove(labelConfig.className);
            }
        }
    }
}
